import type { DirectoryTownSummary } from '@/sanity/directory';
import { toComparableFee } from '@/utils/fee-maths';

import { outwardPostcode } from './directory-profile-heading';

const enrichedDateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const percentFormatter = new Intl.NumberFormat('en-GB', {
  maximumFractionDigits: 1,
  minimumFractionDigits: 0,
});

const flatFeeFormatter = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  maximumFractionDigits: 0,
});

type PublishedFee = {
  percent?: string | null;
  amountPence?: number | null;
} | null;

type FeeTile = {
  isMuted: boolean;
  subtitle: string;
  value: string;
};

export const formatDirectoryAgentHeroMetaLine = (options: {
  openedYear: number | null | undefined;
  postcode: string | null | undefined;
  town: DirectoryTownSummary | null;
}): string | null => {
  const parts: string[] = [];
  const district = outwardPostcode(options.postcode);
  const townName = options.town?.name?.trim();

  if (townName && district) {
    parts.push(`${townName}, ${district}`);
  } else if (townName) {
    parts.push(townName);
  } else if (district) {
    parts.push(district);
  }

  if (options.openedYear != null) {
    parts.push(`Est. ${options.openedYear}`);
  }

  return parts.length === 0 ? null : parts.join(' · ');
};

const looksLikeAcronym = (value: string) => {
  const trimmed = value.trim();
  const firstWord = trimmed.split(/\s+/)[0] ?? '';

  if (firstWord.length >= 2 && firstWord === firstWord.toUpperCase()) {
    return true;
  }

  return trimmed.length > 1 && trimmed === trimmed.toUpperCase();
};

const formatServiceLabel = (value: string, index: number) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (index === 0) {
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  }

  if (looksLikeAcronym(trimmed)) {
    return trimmed;
  }

  return trimmed.charAt(0).toLowerCase() + trimmed.slice(1);
};

export const formatDirectoryAgentHeroServicesLine = (
  services: ReadonlyArray<string | null | undefined> | null | undefined,
): string | null => {
  const seen = new Set<string>();
  const labels: string[] = [];

  for (const service of services ?? []) {
    if (service == null) {
      continue;
    }

    const normalized = service.trim().toLowerCase();
    if (!normalized || seen.has(normalized)) {
      continue;
    }

    seen.add(normalized);
    const label = formatServiceLabel(service, labels.length);
    if (label != null) {
      labels.push(label);
    }
  }

  return labels.length === 0 ? null : labels.join(', ');
};

export const formatDirectoryAgentHeroGoogleLabel = (
  reviewCount: number | null | undefined,
): string => {
  const count = reviewCount ?? 0;
  if (count === 1) {
    return 'Google · 1 review';
  }

  return `Google · ${count} reviews`;
};

export const formatDirectoryAgentHeroHomesLabel = (options: {
  enrichedAt: string | null | undefined;
}): string => {
  if (options.enrichedAt == null) {
    return 'Homes to rent on Rightmove';
  }

  const checked = enrichedDateFormatter.format(new Date(options.enrichedAt));
  return `Homes to rent · Rightmove, ${checked}`;
};

export const getDirectoryAgentHeroFeeTile = (options: {
  fee: PublishedFee;
  feesIncludeVat: boolean | null | undefined;
}): FeeTile => {
  const comparable = toComparableFee({
    fee: options.fee,
    feesIncludeVat: options.feesIncludeVat,
    service: 'management',
  });

  switch (comparable.status) {
    case 'comparable':
      if (comparable.basis === 'percent') {
        return {
          isMuted: false,
          value: `${percentFormatter.format(comparable.percentIncVat)}%`,
          subtitle: 'Full management, inc VAT',
        };
      }

      return {
        isMuted: false,
        value: flatFeeFormatter.format(comparable.amountPenceIncVat / 100),
        subtitle: 'Full management, inc VAT',
      };
    case 'notComparable':
      switch (comparable.reason) {
        case 'vatNotStated': {
          const published =
            comparable.publishedPercent == null
              ? 'Not published'
              : `${comparable.publishedPercent}%`;

          return {
            isMuted: published === 'Not published',
            value: published,
            subtitle: 'Full management, VAT not stated',
          };
        }
        case 'periodNotStated': {
          const published =
            comparable.publishedAmountPence == null
              ? 'Not published'
              : flatFeeFormatter.format(comparable.publishedAmountPence / 100);

          return {
            isMuted: false,
            value: published,
            subtitle: 'Full management, period not stated',
          };
        }
        case 'notPublished':
          return {
            isMuted: true,
            value: 'Not published',
            subtitle: 'Full management fee',
          };
        default: {
          const _exhaustive: never = comparable.reason;
          return _exhaustive;
        }
      }
    default: {
      const _exhaustive: never = comparable;
      return _exhaustive;
    }
  }
};
